import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { apiClient, isMainDomain } from '../api/client';
import { getSocket, disconnectSocket } from '../services/socket';
import { useToast } from './ToastContext';

/**
 * Bell bucket counts. The named buckets are the historical badge keys that UI
 * code reads directly; the index signature carries buckets added later
 * (attendance, eventUpdates, waitlist, members, payments) without needing a
 * change here every time the backend registry grows.
 */
interface NotificationCounts {
  dm: number;
  comments: number;
  approvals: number;
  invitations: number;
  announcements: number;
  reminders: number;
  attendance: number;
  eventUpdates: number;
  waitlist: number;
  members: number;
  payments: number;
  total: number;
  [bucket: string]: number;
}

/** Payload of the `notification` socket event, enriched by the dispatcher. */
interface SocketNotification {
  type: string;
  bucket: string | null;
  count: number;
  counts?: NotificationCounts;
  title?: string;
  body?: string;
  link?: string | null;
}

interface Message {
  _id: string;
  conversation: string;
  sender: { _id: string; name: string };
  content: string;
  createdAt: string;
}

interface ConversationUpdate {
  _id: string;
  lastMessage: {
    content: string;
    sender: string;
    timestamp: string;
  };
}

interface NotificationContextType {
  counts: NotificationCounts;
  refreshCounts: () => Promise<void>;
  markDmRead: (conversationId: string) => Promise<void>;
  markCommentsRead: (eventId: string) => Promise<void>;
  markAllRead: (type?: string) => Promise<void>;
  markInvitationsRead: () => Promise<void>;
  markAnnouncementsRead: () => Promise<void>;
  onNewMessage: (handler: (message: Message, conversation: ConversationUpdate) => void) => () => void;
  onTyping: (handler: (data: { conversationId: string; userId: string }) => void) => () => void;
}

/** Every bell bucket the backend registry can emit. */
const BUCKET_KEYS = [
  'dm', 'comments', 'approvals', 'invitations', 'announcements', 'reminders',
  'attendance', 'eventUpdates', 'waitlist', 'members', 'payments',
] as const;

const EMPTY_COUNTS: NotificationCounts = {
  dm: 0, comments: 0, approvals: 0, invitations: 0, announcements: 0, reminders: 0,
  attendance: 0, eventUpdates: 0, waitlist: 0, members: 0, payments: 0, total: 0,
};

/**
 * Don't toast something the user is already looking at.
 *
 * Direct messages are handled by the chat bubble, and a comment toast while
 * the user reads that very event's thread is noise.
 */
const isSuppressedByRoute = (data: SocketNotification): boolean => {
  const path = window.location.pathname + window.location.search;

  if (data.type === 'direct_message') {
    return path.includes('/communication');
  }
  if (data.type === 'event_comment') {
    const eventId = data.link?.match(/event=(\d+)/)?.[1];
    if (!eventId) return false;
    // Delimiter-anchored: a plain `includes` is a prefix match, so a toast for
    // event 1 was suppressed while viewing event 12, and one for event 12 was
    // not suppressed while viewing event 123.
    return new RegExp(`[?&]event(Id)?=${eventId}(&|$)`).test(
      window.location.search,
    );
  }
  return false;
};

const NotificationContext = createContext<NotificationContextType | null>(null);

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [counts, setCounts] = useState<NotificationCounts>(EMPTY_COUNTS);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Held in a ref so the socket effect below does not depend on `addToast`
  // (which changes identity) and re-subscribe the listener on every render.
  const { addToast } = useToast();
  const toastRef = useRef(addToast);
  useEffect(() => { toastRef.current = addToast; }, [addToast]);

  const refreshCounts = useCallback(async () => {
    const userInfo = localStorage.getItem('userInfo');
    if (!userInfo) return;

    // Don't fetch notifications on main domain (no tenant context)
    if (isMainDomain()) return;

    try {
      const { data } = await apiClient.get<NotificationCounts>('/api/notifications/unread-counts');
      setCounts(data);
    } catch (err: any) {
      // Silently ignore ORG_NOT_FOUND errors (invalid tenant slug)
      if (err?.response?.data?.code === 'ORG_NOT_FOUND') return;
      console.error('Failed to fetch notification counts:', err);
    }
  }, []);

  const markDmRead = useCallback(async (conversationId: string) => {
    const userInfo = localStorage.getItem('userInfo');
    if (!userInfo) return;

    try {
      // Mark all notifications for this conversation as read
      // We use the conversation endpoint in messageRoutes which marks notifications as read
      await apiClient.get(`/api/messages/conversations/${conversationId}`);
      await refreshCounts();
    } catch (err) {
      console.error('Failed to mark DM as read:', err);
    }
  }, [refreshCounts]);

  const markCommentsRead = useCallback(async (eventId: string) => {
    const userInfo = localStorage.getItem('userInfo');
    if (!userInfo) return;

    try {
      await apiClient.put(`/api/notifications/read-event/${eventId}`, {});
      await refreshCounts();
    } catch (err) {
      console.error('Failed to mark comments as read:', err);
    }
  }, [refreshCounts]);

  const markInvitationsRead = useCallback(async () => {
    const userInfo = localStorage.getItem('userInfo');
    if (!userInfo) return;

    try {
      await apiClient.put('/api/notifications/read-all?type=invitations', {});
      await refreshCounts();
    } catch (err) {
      console.error('Failed to mark invitations as read:', err);
    }
  }, [refreshCounts]);

  const markAnnouncementsRead = useCallback(async () => {
    const userInfo = localStorage.getItem('userInfo');
    if (!userInfo) return;

    try {
      await apiClient.put('/api/notifications/read-all?type=announcements', {});
      await refreshCounts();
    } catch (err) {
      console.error('Failed to mark announcements as read:', err);
    }
  }, [refreshCounts]);

  const markAllRead = useCallback(async (type?: string) => {
    const userInfo = localStorage.getItem('userInfo');
    if (!userInfo) return;

    try {
      const query = type ? `?type=${type}` : '';
      await apiClient.put(`/api/notifications/read-all${query}`, {});
      await refreshCounts();
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  }, [refreshCounts]);

  const onNewMessage = useCallback((handler: (message: Message, conversation: ConversationUpdate) => void) => {
    const socket = getSocket();
    if (!socket) return () => {};

    const listener = (data: { message: Message; conversation: ConversationUpdate }) => {
      handler(data.message, data.conversation);
      // Also refresh counts when a new message arrives
      refreshCounts();
    };

    socket.on('new_message', listener);

    return () => {
      socket.off('new_message', listener);
    };
  }, [refreshCounts]);

  const onTyping = useCallback((handler: (data: { conversationId: string; userId: string }) => void) => {
    const socket = getSocket();
    if (!socket) return () => {};

    socket.on('user_typing', handler);

    return () => {
      socket.off('user_typing', handler);
    };
  }, []);

  // Check for login state and set up socket connection
  useEffect(() => {
    const checkAuth = () => {
      const userInfo = localStorage.getItem('userInfo');
      setIsLoggedIn(!!userInfo);
    };

    checkAuth();

    // Listen for storage changes (login/logout in other tabs)
    window.addEventListener('storage', checkAuth);

    return () => {
      window.removeEventListener('storage', checkAuth);
    };
  }, []);

  // Initialize socket and fetch counts when logged in
  useEffect(() => {
    if (isLoggedIn) {
      const socket = getSocket();

      if (socket) {
        // One generic handler for every notification type. The dispatcher sends
        // the full recomputed bucket object, so there is no per-bucket
        // arithmetic to keep in sync here any more.
        socket.on('notification', (data: SocketNotification) => {
          if (data.counts) {
            setCounts({ ...EMPTY_COUNTS, ...data.counts });
          } else if (data.bucket) {
            // Older/other emitters may send only one bucket's count.
            setCounts(prev => {
              const next = { ...prev, [data.bucket as string]: data.count };
              next.total = BUCKET_KEYS.reduce((sum, key) => sum + (next[key] ?? 0), 0);
              return next;
            });
          }

          // Toast the arrival. The server already applied the user's in-app
          // preference, so anything reaching us here is wanted — except while
          // the user is looking at the very thing it concerns.
          if (data.title && !isSuppressedByRoute(data)) {
            toastRef.current?.(
              data.body ? `${data.title} — ${data.body}` : data.title,
              'info',
            );
          }
        });

        // Listen for full count updates (after marking as read)
        socket.on('notification_counts', (data: NotificationCounts) => {
          setCounts({ ...EMPTY_COUNTS, ...data });
        });
      }

      // Fetch initial counts
      refreshCounts();

      return () => {
        if (socket) {
          socket.off('notification');
          socket.off('notification_counts');
        }
      };
    } else {
      disconnectSocket();
      setCounts(EMPTY_COUNTS);
    }
  }, [isLoggedIn, refreshCounts]);

  const value: NotificationContextType = {
    counts,
    refreshCounts,
    markDmRead,
    markCommentsRead,
    markInvitationsRead,
    markAnnouncementsRead,
    markAllRead,
    onNewMessage,
    onTyping,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
